import { useState, type ChangeEvent } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	Typography,
} from '@mui/material';
import { useV2Page } from '../../../Context/V2PageContext';
import { useQueryAnswerPage } from '../../../hooks/useQueryAnswerPage';
import type { V2Attachment, V2FileUploadElement } from '../../../types/V2Types';
import type { V2ElementRendererProps } from './elementProps';

/**
 * Reads currentPage off the HRR context to know which pageId to POST the file
 * against. Renderers normally stay stateless but uploads need a side channel
 * to multipart POST; everything else still flows through `onChange`.
 */
export function FileUploadElement({
	element,
	value,
	errors,
	disabled,
	onChange,
}: V2ElementRendererProps<V2FileUploadElement>) {
	const { currentPage } = useV2Page();
	const { uploadFile } = useQueryAnswerPage();
	const [uploading, setUploading] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const files = (Array.isArray(value) ? value : []) as V2Attachment[];
	const errorMessage = errors[0]?.message;

	const handlePick = async (e: ChangeEvent<HTMLInputElement>) => {
		const input = e.target;
		const picked = Array.from(input.files ?? []);
		if (picked.length === 0 || !currentPage) return;

		setUploadError(null);
		setUploading(true);
		try {
			const uploaded: V2Attachment[] = [];
			for (const file of picked) {
				if (element.maxBytes && file.size > element.maxBytes) {
					setUploadError(`File "${file.name}" exceeds the ${element.maxBytes} byte limit.`);
					continue;
				}
				const response = await uploadFile(currentPage.pageId, element.elementId, file);
				uploaded.push(response);
			}
			const next = [...files, ...uploaded].slice(0, element.maxFiles ?? Infinity);
			onChange(next);
		} catch (err) {
			setUploadError(err instanceof Error ? err.message : 'Upload failed.');
		} finally {
			setUploading(false);
			// Reset the input so re-picking the same file fires a change.
			input.value = '';
		}
	};

	const removeAt = (index: number) => {
		const next = files.filter((_, i) => i !== index);
		onChange(next);
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
			<Typography variant="subtitle1">
				{element.label}
				{element.required && ' *'}
			</Typography>
			{element.description && (
				<Typography variant="body2" color="text.secondary">
					{element.description}
				</Typography>
			)}
			<Button
				variant="outlined"
				component="label"
				disabled={disabled || uploading}
				data-cy={`hrr-element-${element.elementId}`}
				data-element-type="fileUpload">
				{uploading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
				Choose files
				<input
					type="file"
					hidden
					multiple={(element.maxFiles ?? 1) > 1}
					accept={(element.acceptMimeTypes ?? []).join(',')}
					onChange={handlePick}
				/>
			</Button>

			{files.length > 0 && (
				<List dense data-cy={`hrr-uploaded-${element.elementId}`}>
					{files.map((file, i) => (
						<ListItem key={file.attachmentId}>
							<ListItemText primary={file.fileName} secondary={`${file.size} bytes`} />
							<ListItemSecondaryAction>
								<IconButton
									edge="end"
									size="small"
									onClick={() => removeAt(i)}
									aria-label={`Remove ${file.fileName}`}>
									×
								</IconButton>
							</ListItemSecondaryAction>
						</ListItem>
					))}
				</List>
			)}

			{(errorMessage || uploadError) && (
				<Alert severity="error">{uploadError ?? errorMessage}</Alert>
			)}
		</Box>
	);
}

